import React, { useEffect, useMemo, useRef, useState } from "react";
import UserOpsModalShell from "./UserOpsModalShell";
import { RWANDA_DISTRICTS } from "../rwandaDistricts";

const ROLES = [
  { id: "user", label: "User", hint: "Standard access" },
  { id: "agent", label: "Agent", hint: "Field operator" },
  { id: "ambassador", label: "Ambassador", hint: "Community contact" },
  { id: "partner", label: "Partner", hint: "Partner desk" },
  { id: "admin", label: "Admin", hint: "Console access" },
  { id: "super_admin", label: "Super", hint: "Full control" },
];

export default function UserRoleModal({ open, user, loading, onClose, onConfirm }) {
  const [role, setRole] = useState(user?.role || "user");
  const [contactPhone, setContactPhone] = useState("");
  const [location, setLocation] = useState("");
  const ambassadorFieldsRef = useRef(null);

  const accountPhone = user?.phoneNumber || "";
  const usesAccountPhone = Boolean(accountPhone) && role === "ambassador";
  const isAmbassador = role === "ambassador";

  useEffect(() => {
    if (!open || !user) return;
    setRole(user.role || "user");
    setContactPhone(user.ambassadorContactPhone || user.phoneNumber || "");
    setLocation(user.ambassadorLocation || "");
  }, [open, user]);

  useEffect(() => {
    if (!open || !isAmbassador) return;
    ambassadorFieldsRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [open, isAmbassador]);

  const validationError = useMemo(() => {
    if (role !== "ambassador") return "";
    if (!String(location || "").trim()) return "Select a district for this ambassador.";
    if (!usesAccountPhone && !String(contactPhone || "").trim()) {
      return "Contact phone is required for email-based accounts.";
    }
    return "";
  }, [role, location, usesAccountPhone, contactPhone]);

  if (!user) return null;

  const unchanged =
    role === user.role &&
    (role !== "ambassador" ||
      (String(location || "").trim() === String(user.ambassadorLocation || "").trim() &&
        (usesAccountPhone ||
          String(contactPhone || "").trim() ===
            String(user.ambassadorContactPhone || user.phoneNumber || "").trim())));

  const handleConfirm = () => {
    if (validationError) return;
    onConfirm(user._id, role, {
      ambassadorContactPhone: usesAccountPhone ? undefined : contactPhone.trim(),
      ambassadorLocation: location.trim(),
    });
  };

  return (
    <UserOpsModalShell
      open={open}
      onClose={onClose}
      eyebrow="Role desk"
      title="Change role"
      tone="role"
      user={user}
      ariaLabel="Change user role"
      footer={
        <>
          <button type="button" className="au-modal-btn au-modal-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="au-modal-btn au-modal-btn-primary"
            disabled={loading || unchanged || Boolean(validationError)}
            onClick={handleConfirm}
          >
            {loading ? "Saving…" : unchanged ? "No change" : "Apply role"}
          </button>
        </>
      }
    >
      <p className="au-modal-lead">Select the access tier for this account.</p>
      <div className="au-modal-role-grid" role="radiogroup" aria-label="Role options">
        {ROLES.map((opt) => {
          const active = role === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={active}
              className={`au-modal-role-opt${active ? " au-modal-role-opt-active" : ""}${
                user.role === opt.id ? " au-modal-role-opt-current" : ""
              }`}
              onClick={() => setRole(opt.id)}
            >
              <span className="au-modal-role-opt-label">{opt.label}</span>
              <span className="au-modal-role-opt-hint">{opt.hint}</span>
              {user.role === opt.id ? (
                <span className="au-modal-role-opt-tag">Current</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {isAmbassador ? (
        <div ref={ambassadorFieldsRef} className="au-modal-ambassador-fields">
          <p className="au-modal-lead">Assign ambassador location and contact details.</p>

          <label className="au-modal-field">
            <span className="au-modal-field-label">Location</span>
            <span className="au-modal-field-hint">Select one of Rwanda&apos;s 30 districts</span>
            <select
              className="au-modal-input au-modal-select"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              required
              aria-required="true"
            >
              <option value="">Select district</option>
              {RWANDA_DISTRICTS.map((district) => (
                <option key={district} value={district}>
                  {district}
                </option>
              ))}
            </select>
          </label>

          {usesAccountPhone ? (
            <div className="au-modal-field">
              <span className="au-modal-field-label">Contact phone</span>
              <p className="au-modal-field-note">
                Using account phone: <strong>{accountPhone}</strong>
              </p>
            </div>
          ) : (
            <label className="au-modal-field">
              <span className="au-modal-field-label">Contact phone</span>
              <input
                type="tel"
                className="au-modal-input"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="2507XXXXXXXX"
                autoComplete="tel"
              />
            </label>
          )}

          {validationError ? (
            <p className="au-modal-field-error" role="alert">
              {validationError}
            </p>
          ) : null}
        </div>
      ) : null}
    </UserOpsModalShell>
  );
}
